export type SeoContentBlock = {
  title: string;
  body: string;
};

export type SeoFaq = {
  question: string;
  answer: string;
};

export const menuSeoHighlights: SeoContentBlock[] = [
  {
    title: "Tuscan Cove Bar & Patio menu in Southern Highlands",
    body:
      "The Tuscan Cove Bar & Patio menu gives Southern Highlands and Tuscan Cove Las Vegas guests one place to compare dinner, stone-fired pizza, burgers, wings, Sunday brunch, drinks, catering trays, and private-event food before choosing a table or placing an order.",
  },
  {
    title: "What to order first",
    body:
      "Most first-time guests start with stone-fired pizza, chicken wings, The Smash Burger, grilled shrimp skewers, salads, cocktails, beer, wine, or the Sunday brunch menu. Groups can use the same menu to decide between a casual dinner, happy hour, catering, or a private-event conversation.",
  },
  {
    title: "Built for local restaurant searches",
    body:
      "For guests searching Tuscan Cove Las Vegas, Tuscan Cove Bar & Patio menu, restaurants in Southern Highlands, pizza near South Point, or catering Southern Highlands, the menu connects the actual dishes to the restaurant address at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141.",
  },
  {
    title: "Menu signals local search engines expect",
    body:
      "The menu gives crawlers and guests real dish context: Caesar salad, chicken wings, chicken tenders, chicken parmesan, grilled shrimp skewers, carne asada, the Double Smash Burger, pepperoni pizza, brunch drinks, catering trays, and chef-led restaurant service in Southern Highlands.",
  },
];

export const menuSeoFaqs: SeoFaq[] = [
  {
    question: "What is on the Tuscan Cove Bar & Patio menu?",
    answer:
      "The Tuscan Cove Bar & Patio menu includes stone-fired pizza, wings, burgers, salads, sandwiches, brunch plates, cocktails, beer, wine, catering trays, and group-friendly food in Southern Highlands.",
  },
  {
    question: "Does Tuscan Cove serve Sunday brunch in Las Vegas?",
    answer:
      "Yes. Tuscan Cove serves brunch on Sunday only from its Southern Highlands location, with brunch plates, sweets, sides, mimosas, Bloody Marys, and other brunch drinks.",
  },
  {
    question: "Can I use the menu for catering or private events?",
    answer:
      "Yes. The menu is a useful starting point for catering, private dining, corporate events, rehearsal dinners, birthdays, and group meals. Use the catering or private-events form when the order needs timing, headcount, or space planning.",
  },
  {
    question: "What are the brunch options available at Tuscan Cove?",
    answer:
      "Sunday brunch includes breakfast plates, savory brunch items, sweets, sides, brunch cocktails, mimosas, Bloody Marys, and other current brunch menu items served from the Southern Highlands restaurant.",
  },
  {
    question: "What types of pizza does Tuscan Cove offer?",
    answer:
      "Tuscan Cove serves stone-fired pizza options such as cheese, pepperoni, supreme, margarita, and Buffalo chicken, with menu availability subject to the current restaurant menu.",
  },
  {
    question: "Can I view the full menu for Tuscan Cove?",
    answer:
      "Yes. The menu page is the place to review Tuscan Cove Bar & Patio menu sections for starters, salads, stone-fired pizza, sandwiches, mains, brunch, drinks, catering, and popular dishes.",
  },
  {
    question: "What are the brunch hours at Tuscan Cove?",
    answer:
      "Brunch is available Sunday only. Call (725) 223-0714 for the current brunch service window before visiting.",
  },
  {
    question: "Are there vegetarian options on the brunch menu?",
    answer:
      "Yes. Vegetarian-friendly starting points can include salads, avocado toast, sweets, sides, and select brunch or pizza items. Ask the team about current modifications when ordering.",
  },
  {
    question: "What is included in the brunch special?",
    answer:
      "Brunch offerings can include egg dishes, savory brunch plates, sweets, sides, mimosas, Bloody Marys, and espresso martinis. Check the current menu before visiting because dishes and specials can change.",
  },
  {
    question: "Does Tuscan Cove offer bottomless mimosas?",
    answer:
      "The current menu highlights brunch drinks such as mimosas rather than promising a standing bottomless mimosa special. Call (725) 223-0714 for current brunch drink availability.",
  },
  {
    question: "Is Tuscan Cove the same as Piesanos?",
    answer:
      "No. Tuscan Cove Bar & Patio is its own Southern Highlands restaurant at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141. Guests comparing Piesanos or other local pizza options should use this menu for Tuscan Cove's current dishes.",
  },
];

export const privateEventsSeoSections: SeoContentBlock[] = [
  {
    title: "What kinds of events fit at Tuscan Cove Bar & Patio",
    body:
      "Tuscan Cove works well for groups that want a neighborhood restaurant feel in Southern Highlands. Rehearsal dinners, milestone birthdays, anniversary dinners, retirement parties, corporate team dinners, client gatherings, and holiday celebrations all settle in comfortably here. The setting is the same bar and patio our regulars know, with stone-fired pizza, burgers, salads, and craft cocktails as the through line. Guests get a sense of place, not a generic event room. If you are weighing whether your group is the right shape for our space, share rough numbers and the kind of evening you are picturing, and we can tell you honestly what works.",
  },
  {
    title: "How to plan your private event timeline",
    body:
      "The earlier you start, the more flexibility you have. For wedding-week dinners and large milestone celebrations, four to eight weeks of lead time gives the team room to lock in your date, walk through menu options, and align on timing for arrivals, courses, and any toasts. Smaller corporate dinners and birthday groups often come together in a week or two when calendars are still open. Either way, the planning rhythm is the same. Send your inquiry through the Private Events form, talk through date and headcount, finalize the menu format, and confirm the run of the night. If your date is tight, call (725) 223-0714 and we will tell you quickly what is realistic.",
  },
  {
    title: "Menu formats for groups",
    body:
      "Menus for groups are built off the same kitchen and bar you taste on a regular visit. Common formats include preset multi-course menus designed for the size of your party, family-style platters that make the table feel like a long shared meal, and individual ordering off curated short lists so guests still get a choice. Bar service can be open, host-managed with caps, or a guided cocktail and wine selection that fits the tone of the night. We can lean into stone-fired pizza, burgers, salads, brunch favorites, or a more elevated dinner shape, depending on the occasion. Share dietary needs early and we will route around them in the menu we propose.",
  },
  {
    title: "Private-event food guests already recognize",
    body:
      "Many private-event conversations start with familiar Tuscan Cove dishes instead of an abstract banquet menu. Groups can ask about chicken wings, chicken tenders, pepperoni pizza, chicken parmesan, grilled shrimp skewers, carne asada, the Double Smash Burger, salads, brunch food, bar bites, cocktails, beer, wine, and catering trays as a starting point.",
  },
  {
    title: "Where we are and how guests arrive",
    body:
      "Tuscan Cove is at 12656 Southern Highlands Pkwy in Southern Highlands, Las Vegas, NV 89141. The setting is a real neighborhood block, not a casino or strip-mall corridor, which keeps the evening calm even for larger parties. Parking is on site. Share the address with your group ahead of time so arrivals are smooth. For directions, timing, or last-minute changes the night of your event, the restaurant line is (725) 223-0714.",
  },
];

export const privateEventsSeoFaqs: SeoFaq[] = [
  {
    question: "Is Tuscan Cove one of the restaurants that host private events near me?",
    answer:
      "If you are near Southern Highlands, South Point, Enterprise, or southwest Las Vegas, Tuscan Cove Bar & Patio is a local restaurant that can discuss private events, group dining, corporate events, rehearsal dinners, and catering trays.",
  },
  {
    question: "Does Tuscan Cove offer Las Vegas restaurants with private dining options?",
    answer:
      "Yes. Tuscan Cove supports private dining inquiries in Las Vegas with options such as patio, main dining, full buyout, or not-sure planning so the team can recommend the right format.",
  },
  {
    question: "Can private events include group menus or catering trays?",
    answer:
      "Yes. Private events can start from group menus, catering trays, pizza and wings, appetizers, brunch or shower food, family-style dinner, or another menu direction based on the event.",
  },
  {
    question: "Can I plan corporate events or rehearsal dinners at Tuscan Cove?",
    answer:
      "Yes. Corporate events, rehearsal dinners, client meals, birthdays, showers, holiday parties, and family gatherings can all start through the private-events inquiry form.",
  },
  {
    question: "What events can I host at Tuscan Cove?",
    answer:
      "Tuscan Cove can discuss birthdays, anniversaries, rehearsal dinners, corporate events, client meals, holiday parties, family gatherings, showers, team dinners, and local private parties.",
  },
  {
    question: "How do I book a private event?",
    answer:
      "Start with the private-events inquiry form and include the occasion, preferred date, estimated guest count, timing, space preference, food style, and notes. The team reviews the request before confirming availability.",
  },
  {
    question: "What catering options are available for events?",
    answer:
      "Event food can start with catering trays, pizza and wings, appetizers, salads, brunch items, group menus, family-style dinner, or a more hosted private dining plan depending on the event.",
  },
  {
    question: "Can I customize the event menu?",
    answer:
      "Yes. Menu direction depends on date, headcount, timing, space, dietary needs, and the occasion. Share those details so the team can suggest a practical event menu.",
  },
  {
    question: "What is the maximum guest capacity?",
    answer:
      "Capacity depends on date, room setup, patio or main dining preference, and whether the event needs a full buyout. Send the estimated headcount in the inquiry so the team can confirm the right format.",
  },
  {
    question: "Are there any event planning services offered?",
    answer:
      "Tuscan Cove helps with restaurant-side planning such as space fit, food format, timing, bar direction, and next steps. Outside decor, entertainment, and specialty rentals should be discussed separately.",
  },
  {
    question: "What types of private gatherings are supported?",
    answer:
      "Supported gathering types include corporate dinners, rehearsal dinners, birthday parties, anniversaries, showers, family meals, holiday parties, client dinners, and neighborhood group events.",
  },
  {
    question: "Is there a deposit required for bookings?",
    answer:
      "Deposit, payment, and cancellation terms depend on the event size, date, format, and final plan. The team confirms any required deposit or agreement after reviewing the inquiry.",
  },
];

export const aboutLocalTrustPoints: SeoContentBlock[] = [
  {
    title: "Restaurants Southern Highlands Las Vegas local proof",
    body:
      "Tuscan Cove Bar & Patio is one of the restaurants Southern Highlands Las Vegas guests can verify by exact address, menu depth, neighborhood landmarks, and a direct phone path at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141.",
  },
  {
    title: "Southern Highlands and South Point context",
    body:
      "The restaurant serves Southern Highlands neighbors, South Point visitors, golf groups, families, and southwest Las Vegas guests who want pizza, burgers, wings, brunch, drinks, catering, or a group-friendly restaurant close to home.",
  },
  {
    title: "Why the about page matters",
    body:
      "The about page gives searchers factual trust signals for Tuscan Cove Bar & Patio: exact NAP information, Southern Highlands location context, menu categories, private-event support, catering options, and the public-facing story behind the restaurant.",
  },
  {
    title: "Food, drinks, and atmosphere signals",
    body:
      "The restaurant story is supported by concrete menu and atmosphere details: stone-fired pizza, Caesar salad, chicken wings, pepperoni pizza, chicken parmesan, grilled shrimp skewers, carne asada, the Double Smash Burger, brunch cocktails, patio seating, sports screens, and private-event tables.",
  },
];

export const aboutSeoFaqs: SeoFaq[] = [
  {
    question: "What types of food are offered at Tuscan Cove Bar & Patio?",
    answer:
      "Tuscan Cove serves stone-fired pizza, chicken wings, burgers, sandwiches, salads, mains, brunch plates, cocktails, beer, wine, and group-friendly restaurant food in Southern Highlands.",
  },
  {
    question: "Is there a menu available for Tuscan Cove Bar & Patio?",
    answer:
      "Yes. The Tuscan Cove Bar & Patio menu is available on the menu page with dinner, brunch, drinks, catering, and popular dishes.",
  },
  {
    question: "What is the atmosphere like at Tuscan Cove Bar & Patio?",
    answer:
      "The atmosphere is a polished neighborhood restaurant, bar, and patio with Southern Highlands local energy, sports screens, cocktails, outdoor seating, and group-friendly dining.",
  },
  {
    question: "What are the signature dishes at Tuscan Cove?",
    answer:
      "Popular menu signals include stone-fired pizza, chicken wings, the Double Smash Burger, grilled shrimp skewers, carne asada, Caesar salad, chicken parmesan, and Sunday brunch plates.",
  },
  {
    question: "Are there vegetarian options on the menu?",
    answer:
      "Yes. Vegetarian-friendly starting points can include salads, pizza, avocado toast, sweets, sides, and current menu modifications. Ask the restaurant team about the best current options.",
  },
  {
    question: "What drinks pair well with the food?",
    answer:
      "Guests often pair pizza, burgers, wings, brunch, and sharables with cocktails, beer, wine, mimosas, Bloody Marys, or espresso martinis depending on the meal.",
  },
  {
    question: "Is outdoor seating available at Tuscan Cove?",
    answer:
      "Yes. Tuscan Cove has patio seating in Southern Highlands, with availability depending on weather, timing, and event or reservation needs.",
  },
  {
    question: "What are the restaurant hours of operation?",
    answer:
      "Tuscan Cove is open Monday through Saturday from 4 PM to 9 PM and Sunday from 10 AM to 8 PM.",
  },
];

// DRAFT Roma About copy — review voice before launch.
export const romaAboutLocalTrustPoints: SeoContentBlock[] = [
  {
    title: "Led by Chef Ricardo Romo",
    body:
      "Roma Kitchen is led by executive chef Ricardo Romo, who brings more than 30 years of culinary experience, including Las Vegas Strip kitchens such as Michael Mina at MGM Grand, STK at the Cosmopolitan, Mastro's Ocean Club, Toca Madera, and Beer Park.",
  },
  {
    title: "Two Las Vegas valley dining rooms",
    body:
      "Guests can verify both locations by exact address: Roma Kitchen at 10420 S Decatur Blvd #100, Las Vegas, NV 89141, opened August 2024, and Chef's Roma Kitchen at 1550 W Horizon Ridge Pkwy Ste P, Henderson, NV 89012, opened February 2023.",
  },
  {
    title: "Owned and operated locally",
    body:
      "Both Roma rooms are owned and operated by Steven Kennedy through his Las Vegas hospitality group, Element Hospitality. A Las Vegas resident of more than 25 years with over two decades shaping Las Vegas hospitality, he gives the chef's kitchen a real operating history.",
  },
  {
    title: "Reservations, private dining, and catering",
    body:
      "Southern Highlands takes reservations online on this site and Henderson through Google Reserve, and both locations offer private dining for celebrations and catering for offices and homes.",
  },
];

// DRAFT Roma About copy — review voice before launch.
export const romaAboutSeoFaqs: SeoFaq[] = [
  {
    question: "Who is the chef at Roma Kitchen?",
    answer:
      "Roma Kitchen is led by executive chef Ricardo Romo, who has more than 30 years of culinary experience and cooked in Las Vegas Strip kitchens including Michael Mina at MGM Grand, STK, Mastro's Ocean Club, Toca Madera, and Beer Park before opening an intimate neighborhood Italian room.",
  },
  {
    question: "Why did Chef Ricardo Romo open Roma Kitchen?",
    answer:
      "In his words, \"I have worked on the Strip, which I loved, but I wanted to step away from the glitz and connect with the local community. I want to serve classic, Italian comfort food and good wine in a small, intimate setting.\"",
  },
  {
    question: "Who owns and operates Roma Kitchen?",
    answer:
      "Roma Kitchen is owned and operated by Steven Kennedy through his Las Vegas hospitality group, Element Hospitality. He has spent more than two decades in Las Vegas hospitality and opened Chef's Roma Kitchen in Henderson in February 2023 and Roma Kitchen in Southern Highlands in August 2024.",
  },
  {
    question: "How do I reserve a table at Roma Kitchen?",
    answer:
      "Book Roma Kitchen in Southern Highlands online at chefsromakitchen.com/reservations, or Chef's Roma Kitchen in Henderson through Google Reserve. Call Roma Kitchen at (725) 745-2478 or Chef's Roma Kitchen in Henderson at (725) 240-6153 with questions.",
  },
  {
    question: "Does Roma Kitchen offer private dining?",
    answer:
      "Yes. Roma Kitchen hosts private dining for rehearsal dinners, anniversaries, business dinners, birthdays, and small celebrations at both dining rooms.",
  },
  {
    question: "Does Roma Kitchen offer catering?",
    answer:
      "Yes. Roma Kitchen handles catering requests for offices, homes, and hosted gatherings, with menus built off the same dishes guests order on a regular visit.",
  },
  {
    question: "What is on the Roma Kitchen menu?",
    answer:
      "Roma Kitchen serves Italian classics, pasta, antipasti boards, charred mains, and an Italian wine list by the glass or the bottle.",
  },
  {
    question: "Can I order Roma Kitchen for pickup or delivery?",
    answer:
      "Pickup and delivery, when available, route through Grubhub. Reservations, private dining, and catering are handled through the Roma website and direct team.",
  },
];

export const contactSeoTrustPoints: SeoContentBlock[] = [
  {
    title: "Direct restaurant contact",
    body:
      "Tuscan Cove Bar & Patio can be reached at (725) 223-0714 or steven@tuscancove.com for reservations, private events, catering, directions, operating hours, special events, and general inquiries.",
  },
  {
    title: "Address and service area",
    body:
      "The restaurant is located at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141 and serves Southern Highlands, South Point visitors, Enterprise, and southwest Las Vegas guests.",
  },
  {
    title: "Best page for each request",
    body:
      "Use reservations for normal tables, private events for hosted group dining, catering for trays or off-site food, the menu for current dishes, and the contact form for questions that do not fit those paths.",
  },
];

export const contactSeoFaqs: SeoFaq[] = [
  {
    question: "What dining options does Tuscan Cove offer?",
    answer:
      "Tuscan Cove offers restaurant dining, Sunday brunch, happy hour, stone-fired pizza, burgers, wings, cocktails, patio seating, catering, and private-event options.",
  },
  {
    question: "How can I make a reservation?",
    answer:
      "Use the reservations page for normal table requests or call (725) 223-0714 if you have a timing question, larger party, or special request.",
  },
  {
    question: "What are the operating hours of Tuscan Cove?",
    answer:
      "Tuscan Cove is open Monday through Saturday from 4 PM to 9 PM and Sunday from 10 AM to 8 PM.",
  },
  {
    question: "Is there a private event space available?",
    answer:
      "Yes. Tuscan Cove can discuss patio, main dining, full buyout, or not-sure private-event options for group dining and celebrations.",
  },
  {
    question: "What catering services does Tuscan Cove provide?",
    answer:
      "Tuscan Cove can discuss catering trays, pizza and wings, group menus, brunch food, corporate meals, private parties, and event food based on date, headcount, and timing.",
  },
  {
    question: "How can I contact Tuscan Cove for inquiries?",
    answer:
      "Send the contact form, email steven@tuscancove.com, or call (725) 223-0714 for restaurant questions, private events, catering, directions, and follow-up requests.",
  },
  {
    question: "Are there any special events at Tuscan Cove?",
    answer:
      "Tuscan Cove hosts restaurant events and can discuss private events such as birthdays, corporate meals, rehearsal dinners, holiday parties, and group gatherings.",
  },
  {
    question: "What is the address of Tuscan Cove?",
    answer:
      "Tuscan Cove Bar & Patio is located at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141 in Southern Highlands.",
  },
];

export const privateDiningSemanticFaqs: SeoFaq[] = [
  {
    question: "What types of events can be hosted at Tuscan Cove Bar & Patio?",
    answer:
      "Tuscan Cove can discuss birthdays, anniversaries, corporate dinners, client meals, rehearsal dinners, holiday parties, family gatherings, showers, and local group events.",
  },
  {
    question: "How can I inquire about private dining options?",
    answer:
      "Use the private-events inquiry form or call (725) 223-0714 with the occasion, date, guest count, timing, space preference, and menu direction.",
  },
  {
    question: "What are the advantages of choosing Tuscan Cove for private dining?",
    answer:
      "Tuscan Cove gives groups a Southern Highlands restaurant setting, patio and main dining options, practical parking, familiar menu items, bar service, and a local alternative to Strip or casino venues.",
  },
  {
    question: "What events are ideal for private dining?",
    answer:
      "Private dining is a good fit for birthday dinners, family celebrations, corporate meals, client dinners, rehearsal dinners, holiday parties, and group meals that need planning beyond a normal table.",
  },
  {
    question: "How many guests can Tuscan Cove accommodate?",
    answer:
      "Guest capacity depends on date, room setup, space preference, patio availability, and whether the event needs a full buyout. Send the estimated headcount so the team can confirm fit.",
  },
  {
    question: "Are there customizable menu options available?",
    answer:
      "Yes. The team can discuss menu direction from pizza, wings, burgers, salads, brunch, catering trays, cocktails, beer, and wine based on the event format.",
  },
  {
    question: "What is the reservation process for private dining?",
    answer:
      "Private dining starts with an inquiry, then the team reviews availability, headcount, space needs, menu direction, timing, and any deposit or confirmation requirements.",
  },
  {
    question: "Can I tour the private dining space?",
    answer:
      "A space walkthrough may be possible by arrangement. Use the private-events inquiry form or call the restaurant to ask about timing and availability.",
  },
];
